import React, { useEffect, useState, useRef } from "react"
import { components } from "react-select"
import { uniq, uniqBy } from "../../utils/collectionUtils"
import { MultiValueLabel, Option } from "../../components/pricetable/PPShopPreset"
import { PPDefaultSelector } from "../../components/ui/PPDefaultSelector"
import countries from "i18n-iso-countries"
import i18n_iso_countrie_de from "i18n-iso-countries/langs/de.json"
import i18n_iso_countrie_en from "i18n-iso-countries/langs/en.json"
import PPUtils from "../../utils/PPUtils"
import PPStatisticsInsights from "../../utils/PPStatisticsInsights"

countries.registerLocale(i18n_iso_countrie_en)
countries.registerLocale(i18n_iso_countrie_de)

const SHOP_MULTI_SELECT_COMPONENTS = { MultiValueLabel, Option }

const COLORS = PPUtils.Colors
const DEFAULT_OVERVIEW_SHOP_COUNT = 10
const stackedBarOptions = {
    colors: [COLORS.GreenStart, COLORS.YellowStart, COLORS.RedStart],
    chart: {
        type: "bar",
        stacked: true,
        stackType: "100%",
        toolbar: { show: false },
        zoom: { enabled: false }
    },
    dataLabels: {
        enabled: true,
        formatter: (val) => (val >= 10 ? `${Math.round(val)}%` : ""),
        style: {
            colors: ["#ffffff"],
            fontSize: "11px",
            fontWeight: "bold"
        },
        dropShadow: {
            enabled: true,
            top: 1,
            left: 1,
            blur: 0.6,
            color: "#1c1c1c",
            opacity: 0.8
        }
    },
    plotOptions: {
        bar: { horizontal: true }
    },
    xaxis: {
        type: "category",
        max: 100,
        labels: { formatter: (v) => `${v}%` }
    },
    legend: { show: true, position: "top" },
    tooltip: {
        y: { formatter: (val) => `${Number(val || 0).toFixed(1)}%` }
    }
}

function barHeight(seriesCount) {
    let vbarHeight = 38
    if (seriesCount === 1) vbarHeight = 98
    else if (seriesCount === 2) vbarHeight = 65
    else if (seriesCount === 3) vbarHeight = 54
    else if (seriesCount === 4 || seriesCount === 5) vbarHeight = 48
    else if (seriesCount === 6) vbarHeight = 44
    return Math.max(seriesCount * vbarHeight, 120)
}

function AnimatedBarGrid({ children, itemKeys }) {
    const [readyKeys, setReadyKeys] = useState(new Set())
    const prevKeysRef = useRef(new Set())
    const keysStr = JSON.stringify(itemKeys || [])
    
    useEffect(() => {
        const keys = itemKeys || []
        const currentKeys = new Set(keys)
        const prevKeys = prevKeysRef.current
        const newKeys = keys.filter(k => !prevKeys.has(k))
        
        if (newKeys.length > 0) {
            requestAnimationFrame(() => {
                requestAnimationFrame(() => {
                    setReadyKeys(prev => {
                        const next = new Set(prev)
                        newKeys.forEach(k => next.add(k))
                        return next
                    })
                })
            })
        }
        
        const removedKeys = [...prevKeys].filter(k => !currentKeys.has(k))
        if (removedKeys.length > 0) {
            setReadyKeys(prev => {
                const next = new Set(prev)
                removedKeys.forEach(k => next.delete(k))
                return next
            })
        }
        
        prevKeysRef.current = currentKeys
    }, [keysStr])
    
    const isReady = (key) => readyKeys.has(key)
    return children(isReady)
}

function langOption(lang, i18n) {
    if (!lang) return null
    return {
        label: countries.getName(lang, i18n.language) || String(lang).toUpperCase(),
        value: lang,
        lang
    }
}

/** First language that appears in the given items (optionally prefer one). */
function firstAvailableLang(items, preferredLang) {
    const langs = uniq((items || []).map((x) => x?.lang).filter(Boolean))
    if (langs.length === 0) return preferredLang || null
    if (preferredLang && langs.includes(preferredLang)) return preferredLang
    return langs[0]
}

/**
 * Local language multi-filter, independent per panel.
 * Defaults to the first language in `data` (or preferredLang if present there).
 */
function useLocalLangFilter(data, i18n, preferredLang) {
    const [langFilter, setLangFilter] = useState(() => {
        const lang = firstAvailableLang(data, preferredLang)
        const opt = langOption(lang, i18n)
        return opt ? [opt] : []
    })
    const dataKey = JSON.stringify(uniq((data || []).map((x) => x?.lang).filter(Boolean)))

    useEffect(() => {
        const lang = firstAvailableLang(data, preferredLang)
        if (!lang) return
        setLangFilter((current) => {
            if (current && current.length > 0) {
                const stillValid = current.filter((o) => (data || []).some((x) => x?.lang === o.value))
                if (stillValid.length > 0) return stillValid
            }
            const opt = langOption(lang, i18n)
            return opt ? [opt] : []
        })
    }, [dataKey, preferredLang, i18n.language])

    return [langFilter, setLangFilter]
}

function LangSelector({ id, data, langFilter, setLangFilter, i18n }) {
    const languages = uniqBy(
        (data || []).filter(Boolean).map((x) => ({
            label: countries.getName(x.lang, i18n.language) || (x.lang || "").toUpperCase(),
            value: x.lang,
            lang: x.lang
        })),
        (e) => e.value
    )
    if (languages.length <= 1) return null
    return (
        <PPDefaultSelector
            id={id}
            isMulti={true}
            onChange={setLangFilter}
            data={languages}
            value={langFilter}
            defaultValue={langFilter}
            components={{ MultiValueLabel, Option }}
        />
    )
}

const SingleValueWithFlag = ({ children, ...props }) => {
    const { data } = props
    return (
        <components.SingleValue {...props}>
            {data.lang && <span className={"fi fi-" + data.lang} style={{ marginRight: "6px" }} />}
            {children}
        </components.SingleValue>
    )
}

function buildShopOptions(shopStats, langFilter) {
    const filtered = PPStatisticsInsights.filterByLang(
        PPStatisticsInsights.competitorShops(shopStats),
        langFilter
    )
    return filtered.map((s) => ({
        label: `${s.name} (${(s.lang || "").toUpperCase()})`,
        value: s.shopId,
        shopId: s.shopId,
        lang: s.lang
    }))
}

export { SHOP_MULTI_SELECT_COMPONENTS, COLORS, DEFAULT_OVERVIEW_SHOP_COUNT, stackedBarOptions, barHeight, AnimatedBarGrid, langOption, firstAvailableLang, useLocalLangFilter, LangSelector, SingleValueWithFlag, buildShopOptions }
